import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  buildAuthenticationClickedProps,
  mapAuthProviderToProvider,
} from '@/models/analytics/features/user';
import { getPersistedAuthProvider } from '@/utils/auth/persistedAuthProvider';
import { setSignInMethod } from '@/utils/signInSessionTracking';
import { Button } from '@heidi/design-system/button';
import { Loader } from '@heidi/snowflake-ui/loader';
import { Smartphone } from 'lucide-react';
import { useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

const heidiAppLoginButtonMessages = defineMessages({
  continueWithHeidiApp: {
    defaultMessage: 'Continue with Heidi app',
    id: 'P48wY/AD1K',
    description:
      'Button label for starting Login with Heidi App CIBA approval from the sign-in modal',
  },
});

interface HeidiAppLoginButtonProps {
  email: string;
  isEmailInvalid: boolean;
  /** External disabled state (e.g., when another login button is in progress) */
  disabled: boolean;
  /** Called when the button is pressed with a valid email — proceeds with CIBA login. */
  onProceed: () => void;
  /** Called when the button is pressed without a valid email — surfaces the email validation error. */
  onInvalidEmail: () => void;
}

export function HeidiAppLoginButton({
  email,
  isEmailInvalid,
  disabled,
  onProceed,
  onInvalidEmail,
}: HeidiAppLoginButtonProps) {
  const [isLoading, setIsLoading] = useState(false);

  // Mirror the "Continue" (email) button: the button stays clickable even when the
  // email is empty/invalid, and on press surfaces the email validation error instead
  // of silently disabling. Only the in-progress state disables the button.
  const handleClick = () => {
    if (!email || isEmailInvalid) {
      onInvalidEmail();
      return;
    }
    /*
     * Same authentication_clicked event the analyticsScribe.signInClicked
     * model method fires for the other methods, via the sanctioned track()
     * facade (new dispatch.analyticsScribe call sites are frozen). Provider
     * comes from persisted storage because the Redux store is unavailable
     * pre-login on this path.
     */
    track({
      event_name: TrackingEventName.AuthenticationClicked,
      props: buildAuthenticationClickedProps({
        clickAction: 'heidi_app',
        provider: mapAuthProviderToProvider(getPersistedAuthProvider()),
      }),
    });
    setSignInMethod('heidi_app');
    setIsLoading(true);
    onProceed();
  };

  return (
    <Button
      disableAutomaticSvgSizing
      className="w-full"
      variant="secondary"
      size="lg"
      type="button"
      disabled={disabled || isLoading}
      onClick={handleClick}
      data-testid="heidi-app-login-button"
    >
      {isLoading ? <Loader className="size-4.5" /> : <Smartphone className="size-4.5" />}
      <FormattedMessage {...heidiAppLoginButtonMessages.continueWithHeidiApp} />
    </Button>
  );
}
